import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { deriveMode } from '@/shared/mode/deriveMode';

/**
 * Scroll the document to the top when the user navigates FORWARD to a
 * different in-mode URL.
 *
 * Rules:
 * - PUSH within the same mode (e.g. /instaflix → /instaflix/profile/x):
 *   scroll to top — that's a "new screen" from the user's perspective.
 * - PUSH across modes (mode switch): don't scroll. Pane.tsx's scroll memo
 *   takes over and restores that pane's last scroll position.
 * - POP (back/forward button): don't scroll. Pane memo + browser-natural
 *   behavior restore the previous position.
 * - REPLACE: don't scroll. Side-grade nav shouldn't disorient the user.
 */
export function useScrollToTopOnNav() {
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  const prevModeRef = useRef(deriveMode(pathname));

  useEffect(() => {
    const currentMode = deriveMode(pathname);
    const sameMode = currentMode === prevModeRef.current;
    prevModeRef.current = currentMode;

    if (sameMode && navigationType === 'PUSH') {
      // Use scrollTo with behavior:'instant' so the new screen appears at
      // the top immediately (a smooth animated jump from 800px down to 0
      // is jarring on mobile).
      window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
    }
  }, [pathname, navigationType]);
}
